import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { api } from '../services/api';

interface User {
  userId: string;
  email: string;
  givenName: string;
  familyName: string;
  group: string;
  status: string;
  enabled: boolean;
}

export default function AdminPage() {
  const navigate = useNavigate();
  const { isAdmin, isAuthenticated } = useAuthStore();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'pending' | 'active'>('pending');
  const [confirmDialog, setConfirmDialog] = useState<{
    show: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  } | null>(null);
  
  // Security: Redirect non-admin users immediately
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    if (!isAdmin) {
      navigate('/feed', { replace: true });
      return;
    }
  }, [isAdmin, isAuthenticated, navigate]);

  // All hooks must be called before any conditional returns
  const { data: usersData, isLoading, error, isError } = useQuery({
    queryKey: ['admin-users', tab],
    queryFn: () => api.listUsers(tab),
    enabled: isAdmin && isAuthenticated, // Only fetch if user is admin
  });

  const users: User[] = usersData?.users || [];

  const approveMutation = useMutation({
    mutationFn: (userId: string) => api.approveUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });

  const changeRoleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      api.changeUserRole(userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });

  const deregisterMutation = useMutation({
    mutationFn: (userId: string) => api.deregisterUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });

  // Security check: Don't render anything if not admin (after all hooks)
  if (!isAuthenticated || !isAdmin) {
    return null;
  }

  const handleApprove = (userId: string) => {
    setConfirmDialog({
      show: true,
      title: 'Approve User',
      message: 'Are you sure you want to approve this user and grant them access to the family album?',
      onConfirm: () => {
        approveMutation.mutate(userId);
        setConfirmDialog(null);
      },
    });
  };

  const handleChangeRole = (userId: string, currentRole: string) => {
    const newRole = currentRole === 'member' ? 'admin' : 'member';
    setConfirmDialog({
      show: true,
      title: 'Change User Role',
      message: `Change this user's role to ${newRole.toUpperCase()}?`,
      onConfirm: () => {
        changeRoleMutation.mutate({ userId, role: newRole });
        setConfirmDialog(null);
      },
    });
  };

  const handleDeregister = (userId: string, email: string) => {
    setConfirmDialog({
      show: true,
      title: 'Deregister User',
      message: `Are you sure you want to deregister ${email}? This action cannot be undone.`,
      onConfirm: () => {
        deregisterMutation.mutate(userId);
        setConfirmDialog(null);
      },
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">User Management</h1>
        <p className="text-gray-600">
          Approve new users and manage existing family members
        </p>
      </div>

      {/* Error Display */}
      {isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-red-800 mb-2">Error loading users</h3>
          <p className="text-sm text-red-600">
            {error instanceof Error ? error.message : 'Unknown error'}
          </p>
          <p className="text-xs text-red-500 mt-2">
            Check browser console (F12) for details. Make sure you signed out and signed back in as admin.
          </p>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setTab('pending')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              tab === 'pending'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Pending Approvals
            {users.length > 0 && tab === 'pending' && (
              <span className="ml-2 px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-semibold">
                {users.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setTab('active')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              tab === 'active'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Active Users
          </button>
        </nav>
      </div>

      {/* Users List */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading users...</div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {tab === 'pending'
              ? 'No pending users'
              : 'No active users'}
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {users.map((user) => (
              <div
                key={user.userId}
                className="p-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {user.givenName} {user.familyName}
                    </h3>
                    <p className="text-sm text-gray-600">{user.email}</p>
                    <div className="mt-2 flex items-center gap-2">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.group === 'super-admin'
                            ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-md'
                            : user.group === 'admin'
                            ? 'bg-purple-100 text-purple-800'
                            : user.group === 'member'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {user.group}
                      </span>
                      <span className="text-xs text-gray-500">
                        {user.status}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    {tab === 'pending' && (
                      <button
                        onClick={() => handleApprove(user.userId)}
                        disabled={approveMutation.isPending}
                        className="btn-primary px-4 py-2.5 text-sm disabled:opacity-50 w-full sm:w-auto"
                      >
                        ✓ Approve
                      </button>
                    )}

                    {tab === 'active' && (
                      <>
                        {user.group === 'super-admin' ? (
                          <span className="px-4 py-2 text-sm text-purple-600 font-medium italic text-center sm:text-left">
                            Super Admin (highest privilege)
                          </span>
                        ) : user.group === 'member' ? (
                          <button
                            onClick={() => handleChangeRole(user.userId, user.group)}
                            disabled={changeRoleMutation.isPending}
                            className="btn-primary px-4 py-2.5 text-sm disabled:opacity-50 w-full sm:w-auto"
                          >
                            Make Admin
                          </button>
                        ) : user.group === 'admin' ? (
                          <button
                            onClick={() => handleChangeRole(user.userId, user.group)}
                            disabled={changeRoleMutation.isPending}
                            className="btn-secondary px-4 py-2.5 text-sm w-full sm:w-auto"
                          >
                            Remove Admin
                          </button>
                        ) : null}
                        
                        {user.group !== 'super-admin' && (
                          <button
                            onClick={() => handleDeregister(user.userId, user.email)}
                            disabled={deregisterMutation.isPending}
                            className="btn-danger px-4 py-2.5 text-sm disabled:opacity-50 w-full sm:w-auto"
                          >
                            Deregister
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      {confirmDialog && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {confirmDialog.title}
            </h3>
            <p className="text-gray-600 mb-6">{confirmDialog.message}</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmDialog(null)}
                className="btn-ghost"
              >
                Cancel
              </button>
              <button
                onClick={confirmDialog.onConfirm}
                className="btn-primary"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

